import React, { useState } from 'react';
import { Trophy, Flame, Target, Award, Calendar, TrendingUp, Gamepad2 } from 'lucide-react';
import XPBar from '../gamification/XPBar';
import LevelBadge from '../gamification/LevelBadge';
import DailyChallenge from '../gamification/DailyChallenge';
import StreakTracker from '../dashboard/StreakTracker';
import MemoryGame from '../minigames/MemoryGame';
import WordScramble from '../minigames/WordScramble';
import MathPuzzle from '../minigames/MathPuzzle';
import { useLanguage } from '../../contexts/LanguageContext';
import '../minigames/minigames.css';

const GameZone = ({ 
  gamificationData, 
  streakData, 
  currentProfile,
  onShowLeaderboard,
  onShowActivityCalendar,
  onGameComplete
}) => {
  const { t } = useLanguage();
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameDifficulty, setGameDifficulty] = useState('easy');

  const handleGameComplete = (result) => {
    console.log('Game completed:', result);
    // Award XP through parent component
    if (onGameComplete) {
      onGameComplete(result);
    }
    // Go back to game selection after a delay
    setTimeout(() => {
      setSelectedGame(null);
    }, 2000);
  };

  const handleBackToGames = () => {
    setSelectedGame(null);
  };

  if (selectedGame === 'memory') {
    return (
      <div className="game-zone animate-fadeIn">
        <div className="game-zone-header">
          <h1 className="game-zone-title">
            <span className="title-icon">🧠</span>
            {t('gameZone.memoryMatch')}
          </h1>
          <button 
            className="action-button hover-lift"
            onClick={handleBackToGames}
            style={{ marginTop: '1rem' }}
          >
            ← {t('common.back')} {t('nav.gameZone')}
          </button>
        </div>
        
        <MemoryGame 
          difficulty={gameDifficulty}
          onComplete={handleGameComplete}
        />
      </div>
    );
  }

  if (selectedGame === 'wordscramble') {
    return (
      <div className="game-zone animate-fadeIn">
        <div className="game-zone-header">
          <h1 className="game-zone-title">
            <span className="title-icon">🎯</span>
            {t('gameZone.wordScramble')}
          </h1>
          <button 
            className="action-button hover-lift"
            onClick={handleBackToGames}
            style={{ marginTop: '1rem' }}
          >
            ← {t('common.back')} {t('nav.gameZone')}
          </button>
        </div>
        
        <WordScramble 
          difficulty={gameDifficulty}
          onComplete={handleGameComplete}
        />
      </div>
    );
  }

  if (selectedGame === 'mathpuzzle') {
    return (
      <div className="game-zone animate-fadeIn">
        <div className="game-zone-header">
          <h1 className="game-zone-title">
            <span className="title-icon">🧩</span>
            {t('gameZone.mathPuzzle')}
          </h1>
          <button 
            className="action-button hover-lift"
            onClick={handleBackToGames}
            style={{ marginTop: '1rem' }}
          >
            ← {t('common.back')} {t('nav.gameZone')}
          </button>
        </div>
        
        <MathPuzzle 
          difficulty={gameDifficulty}
          onComplete={handleGameComplete}
        />
      </div>
    );
  }

  return (
    <div className="game-zone animate-fadeIn">
      <div className="game-zone-header">
        <h1 className="game-zone-title">
          <span className="title-icon">🎮</span>
          {t('gameZone.title')}
        </h1>
        <p className="game-zone-subtitle">
          {t('gameZone.subtitle')}
        </p>
      </div>

      {/* Mini-Games Section */}
      <div className="game-zone-section">
        <h2 className="section-title">
          <Gamepad2 size={24} />
          {t('gameZone.miniGames')}
        </h2>
        
        <div className="minigames-selector">
          <div className="minigame-card hover-lift">
            <div className="minigame-icon">🧠</div>
            <h3>{t('gameZone.memoryMatch')}</h3>
            <p>{t('gameZone.memoryDesc')}</p>
            <div className="difficulty-selector">
              <button 
                className={`difficulty-btn ${gameDifficulty === 'easy' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('easy')}
              >
                {t('difficulty.easy')}
              </button>
              <button 
                className={`difficulty-btn ${gameDifficulty === 'medium' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('medium')}
              >
                {t('difficulty.medium')}
              </button>
              <button 
                className={`difficulty-btn ${gameDifficulty === 'hard' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('hard')}
              >
                {t('difficulty.hard')}
              </button>
            </div>
            <button 
              className="action-button hover-lift"
              onClick={() => setSelectedGame('memory')}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              {t('gameZone.playNow')}
            </button>
          </div>

          <div className="minigame-card hover-lift">
            <div className="minigame-icon">🎯</div>
            <h3>{t('gameZone.wordScramble')}</h3>
            <p>{t('gameZone.wordDesc')}</p>
            <div className="difficulty-selector">
              <button 
                className={`difficulty-btn ${gameDifficulty === 'easy' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('easy')}
              >
                {t('difficulty.easy')}
              </button>
              <button 
                className={`difficulty-btn ${gameDifficulty === 'medium' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('medium')}
              >
                {t('difficulty.medium')}
              </button>
              <button 
                className={`difficulty-btn ${gameDifficulty === 'hard' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('hard')}
              >
                {t('difficulty.hard')}
              </button>
            </div>
            <button 
              className="action-button hover-lift"
              onClick={() => setSelectedGame('wordscramble')}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              {t('gameZone.playNow')}
            </button>
          </div>

          <div className="minigame-card hover-lift">
            <div className="minigame-icon">🧩</div>
            <h3>{t('gameZone.mathPuzzle')}</h3>
            <p>{t('gameZone.mathDesc')}</p>
            <div className="difficulty-selector">
              <button 
                className={`difficulty-btn ${gameDifficulty === 'easy' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('easy')}
              >
                {t('difficulty.easy')}
              </button>
              <button 
                className={`difficulty-btn ${gameDifficulty === 'medium' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('medium')}
              >
                {t('difficulty.medium')}
              </button>
              <button 
                className={`difficulty-btn ${gameDifficulty === 'hard' ? 'active' : ''}`}
                onClick={() => setGameDifficulty('hard')}
              >
                {t('difficulty.hard')}
              </button>
            </div>
            <button 
              className="action-button hover-lift"
              onClick={() => setSelectedGame('mathpuzzle')}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              {t('gameZone.playNow')}
            </button>
          </div>
        </div>
      </div>

      {/* Level & XP Section */}
      <div className="game-zone-section">
        <h2 className="section-title">
          <TrendingUp size={24} />
          {t('gameZone.levelProgress')}
        </h2>
        
        <div className="level-xp-container">
          <div className="level-display">
            <LevelBadge 
              totalXP={gamificationData.totalXP || 0} 
              size="large" 
              showTitle={true} 
            />
          </div>
          
          <div className="xp-display">
            <XPBar 
              totalXP={gamificationData.totalXP || 0} 
              showDetails={true} 
            />
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="game-zone-section">
        <h2 className="section-title">
          <Award size={24} />
          {t('gameZone.quickStats')}
        </h2>
        
        <div className="quick-stats-grid">
          <div className="stat-card hover-lift">
            <div className="stat-icon" style={{ background: '#3498db' }}>
              <TrendingUp size={32} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{gamificationData.totalXP || 0}</div>
              <div className="stat-label">{t('gamification.totalXP')}</div>
            </div>
          </div>

          <div className="stat-card hover-lift">
            <div className="stat-icon" style={{ background: '#e74c3c' }}>
              <Flame size={32} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{streakData.currentStreak || 0}</div>
              <div className="stat-label">{t('streak.dayStreak')}</div>
            </div>
          </div>

          <div className="stat-card hover-lift">
            <div className="stat-icon" style={{ background: '#f39c12' }}>
              <Target size={32} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{streakData.longestStreak || 0}</div>
              <div className="stat-label">{t('streak.longestStreak')}</div>
            </div>
          </div>

          <div className="stat-card hover-lift">
            <div className="stat-icon" style={{ background: '#2ecc71' }}>
              <Calendar size={32} color="white" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{streakData.totalDaysPlayed || 0}</div>
              <div className="stat-label">{t('gameZone.daysPlayed')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Streak Section */}
      <div className="game-zone-section">
        <h2 className="section-title">
          <Flame size={24} />
          {t('gameZone.learningStreak')}
        </h2>
        
        <StreakTracker 
          streakData={streakData} 
          onShowCalendar={onShowActivityCalendar}
        />
      </div>

      {/* Daily Challenges */}
      <div className="game-zone-section">
        <h2 className="section-title">
          <Target size={24} />
          {t('gamification.dailyChallenges')}
        </h2>
        
        {currentProfile && (
          <DailyChallenge profileId={currentProfile.id} />
        )}
      </div>

      {/* Leaderboard Access */}
      <div className="game-zone-section">
        <h2 className="section-title">
          <Trophy size={24} />
          {t('gameZone.competition')}
        </h2>
        
        <div className="leaderboard-card">
          <div className="leaderboard-info">
            <h3>🏆 {t('gameZone.leaderboard')}</h3>
            <p>{t('gameZone.leaderboardDesc')}</p>
          </div>
          <button 
            className="action-button hover-lift"
            onClick={onShowLeaderboard}
          >
            <Trophy size={20} />
            {t('gamification.viewLeaderboard')}
          </button>
        </div>
      </div>

      {/* Tips & Info */}
      <div className="game-zone-section">
        <div className="tips-card">
          <h3>💡 {t('gameZone.howToEarnXP')}</h3>
          <ul className="tips-list">
            <li>{t('gameZone.tip1')}</li>
            <li>{t('gameZone.tip2')}</li>
            <li>{t('gameZone.tip3')}</li>
            <li>{t('gameZone.tip4')}</li>
            <li>{t('gameZone.tip5')}</li>
            <li>{t('gameZone.tip6')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GameZone;
